import type React from 'react'
import CollateralDarkIcon from 'assets/product-page/collateral-dark-icon.svg?react'
import CollateralLightIcon from 'assets/product-page/collateral-light-icon.svg?react'
import CoverageDarkIcon from 'assets/product-page/coverage-dark-icon.svg?react'
import CoverageLightIcon from 'assets/product-page/coverage-light-icon.svg?react'
import RobustDarkIcon from 'assets/product-page/robust-dark-icon.svg?react'
import RobustLightIcon from 'assets/product-page/robust-light-icon.svg?react'
import AiPoweredDarkIcon from 'assets/product-page/ai-powered-dark-icon.svg?react'
import AiPoweredLightIcon from 'assets/product-page/ai-powered-light-icon.svg?react'
import { useWindowSize } from 'hooks'
import type { ProductMetadata } from 'indices-list'
import { useStore } from 'services/store'
import { GLOBAL_COLORS } from 'theme'
import { Separator } from '../switch-sections/styled'
import {
  Container,
  ExposureText,
  GradientText,
  InfoIconItem,
  InfoIconsContainer,
  StyledAiPoweredIcon,
  StyledSvgIcon,
  StyledTitle,
} from './styled'
import { ARTXAI, CAI } from 'products'

const getExposureData = (product: string, gradientColor: string, isDark: boolean) => {
  const aiPoweredIcon = isDark ? AiPoweredDarkIcon : AiPoweredLightIcon
  const coverageIcon = isDark ? CoverageDarkIcon : CoverageLightIcon
  const collateralIcon = isDark ? CollateralDarkIcon : CollateralLightIcon
  const robustIcon = isDark ? RobustDarkIcon : RobustLightIcon

  const coverageText =
    product === 'ARTXAI'
      ? 'ARTX proprietary AI systems automatically incorporates blockchain data, prices and social sentiment in order to outperform the benchmarks'
      : product === 'CAI'
        ? `${product} offers comprehensive access to the largest assets on Avalanche`
        : product === 'CTEST'
          ? `${product} provides multi-chain access to the largest AI agent tokens`
          : `${product} provides multi-chain access to the largest memecoins in crypto`
  const coverageTitle = product !== 'ARTXAI' ? 'Complete sector coverage' : 'AI-Powered'

  const methodologyTitle = product !== 'ARTXAI' ? 'Robust Methodology' : 'Battle tested methodology'
  const methodologyText =
    product !== 'ARTXAI'
      ? 'Each constituent asset is subject to a full and transparent assessment'
      : 'ARTX models have been in production for more than three years, in a continuous reinforced learning process.'

  return [
    {
      icon: product !== 'ARTXAI' ? coverageIcon : aiPoweredIcon,
      title: (
        <StyledTitle>
          <GradientText isCapitalized color={gradientColor}>
            {coverageTitle.split(' ').slice(0, 1).join(' ')}
          </GradientText>{' '}
          {coverageTitle.split(' ').slice(1, coverageTitle.length).join(' ')}
        </StyledTitle>
      ),
      text: coverageText,
      isBigger: true,
    },
    {
      icon: collateralIcon,
      title: (
        <StyledTitle>
          <GradientText isCapitalized color={GLOBAL_COLORS.secondaryGreen}>
            100%
          </GradientText>
          Collateralised
        </StyledTitle>
      ),
      text: `${product} is fully backed and redeemable for its underlying assets `,
    },
    {
      icon: robustIcon,
      title: (
        <StyledTitle>
          <GradientText isCapitalized color={'#0023AD'}>
            {methodologyTitle
              .split(' ')
              .slice(0, product !== 'ARTXAI' ? 1 : 2)
              .join(' ')}
          </GradientText>
          {methodologyTitle
            .split(' ')
            .slice(product !== 'ARTXAI' ? 1 : 2, methodologyTitle.length)
            .join(' ')}
        </StyledTitle>
      ),
      text: methodologyText,
    },
  ]
}

export const TotalExposure: React.FC<{ product: ProductMetadata }> = ({ product }) => {
  const { isDarkMode } = useStore()
  const symbol = product.symbol.toUpperCase()

  const color = symbol !== 'ARTXAI' ? CAI.brandColour : ARTXAI.brandColour

  const exposureData = getExposureData(symbol, color, isDarkMode)
  const { isMobile } = useWindowSize()

  return (
    <Container>
      {!isMobile && <Separator />}
      <InfoIconsContainer>
        {exposureData.map(({ icon, title, text, isBigger }) => (
          <InfoIconItem key={text}>
            {isBigger ? (
              <>
                <StyledAiPoweredIcon icon={icon} />
              </>
            ) : (
              <>
                <StyledSvgIcon icon={icon} />
              </>
            )}
            {title}
            <ExposureText>{text}</ExposureText>
          </InfoIconItem>
        ))}
      </InfoIconsContainer>
    </Container>
  )
}
